/**
 * content.ts
 * Products, Portfolio, News, and WhereToBuy cache management.
 */

import { Product } from '../products';
import { PortfolioProject } from '../portfolio-data';
import { PublicNewsItem, PublicWTBPoint } from '../dataSource';
import { cache, inFlight, isStale, notify } from './base';

const LS_WTB_KEY = 'sargo_where_to_buy';
const LS_PRODUCTS_KEY = 'sargo_products_v1';
const LS_PORTFOLIO_KEY = 'sargo_portfolio_v1';
const LS_NEWS_KEY = 'sargo_news_v1';

// Generic safe localStorage save — silently swallows quota / privacy-mode errors.
function safeSetLS<T>(key: string, data: T, fetchedAt: number): void {
  try {
    localStorage.setItem(key, JSON.stringify({ data, fetchedAt }));
  } catch { /* quota / privacy mode */ }
}

function safeReadLS<T>(key: string): { data: T; fetchedAt: number } | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'data' in parsed && 'fetchedAt' in parsed) {
      return parsed as { data: T; fetchedAt: number };
    }
    return null;
  } catch {
    return null;
  }
}

// ─── Products ─────────────────────────────────────────────────────────────────

export function getCachedProducts(): Product[] | null {
  return cache.products?.data ?? null;
}

export async function fetchProducts(): Promise<Product[]> {
  if (cache.products && !isStale(cache.products)) {
    return cache.products.data;
  }
  if (inFlight.products) return inFlight.products;

  const { getProductsFromDb } = await import('../dataSource');
  inFlight.products = getProductsFromDb().then((data) => {
    const fetchedAt = Date.now();
    cache.products = { data, fetchedAt };
    safeSetLS(LS_PRODUCTS_KEY, data, fetchedAt);
    inFlight.products = null;
    notify();
    return data;
  }).catch(() => {
    inFlight.products = null;
    return cache.products?.data ?? [];
  });

  return inFlight.products;
}

export function invalidateProductsCache() {
  cache.products = null;
  inFlight.products = null;
  try { localStorage.removeItem(LS_PRODUCTS_KEY); } catch { /* ignore */ }
}

// ─── Portfolio ────────────────────────────────────────────────────────────────

export function getCachedPortfolio(): PortfolioProject[] | null {
  return cache.portfolio?.data ?? null;
}

export async function fetchPortfolio(): Promise<PortfolioProject[]> {
  if (cache.portfolio && !isStale(cache.portfolio)) {
    return cache.portfolio.data;
  }
  if (inFlight.portfolio) return inFlight.portfolio;

  const { getPortfolioFromDb } = await import('../dataSource');
  inFlight.portfolio = getPortfolioFromDb().then((data) => {
    const sorted = [...data].sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    });
    const fetchedAt = Date.now();
    cache.portfolio = { data: sorted, fetchedAt };
    safeSetLS(LS_PORTFOLIO_KEY, sorted, fetchedAt);
    inFlight.portfolio = null;
    notify();
    return sorted;
  }).catch(() => {
    inFlight.portfolio = null;
    return cache.portfolio?.data ?? [];
  });

  return inFlight.portfolio;
}

export function invalidatePortfolioCache() {
  cache.portfolio = null;
  inFlight.portfolio = null;
  try { localStorage.removeItem(LS_PORTFOLIO_KEY); } catch { /* ignore */ }
}

// ─── News ─────────────────────────────────────────────────────────────────────

export function getCachedNews(): PublicNewsItem[] | null {
  return cache.news?.data ?? null;
}

export async function fetchNews(): Promise<PublicNewsItem[]> {
  if (cache.news && !isStale(cache.news)) {
    return cache.news.data;
  }
  if (inFlight.news) return inFlight.news;

  const { getNewsFromDb } = await import('../dataSource');
  inFlight.news = getNewsFromDb().then((data) => {
    const fetchedAt = Date.now();
    cache.news = { data, fetchedAt };
    safeSetLS(LS_NEWS_KEY, data, fetchedAt);
    inFlight.news = null;
    notify();
    return data;
  }).catch(() => {
    inFlight.news = null;
    return cache.news?.data ?? [];
  });

  return inFlight.news;
}

export function invalidateNewsCache() {
  cache.news = null;
  inFlight.news = null;
  try { localStorage.removeItem(LS_NEWS_KEY); } catch { /* ignore */ }
}

// ─── WhereToBuy ───────────────────────────────────────────────────────────────

export function getCachedWhereToBuy(): PublicWTBPoint[] | null {
  return cache.whereToBuy?.data ?? null;
}

export async function fetchWhereToBuy(): Promise<PublicWTBPoint[]> {
  if (cache.whereToBuy && !isStale(cache.whereToBuy)) {
    return cache.whereToBuy.data;
  }
  if (inFlight.whereToBuy) return inFlight.whereToBuy;

  const { getWhereToBuyFromDb } = await import('../dataSource');
  inFlight.whereToBuy = getWhereToBuyFromDb().then((data) => {
    const fetchedAt = Date.now();
    cache.whereToBuy = { data, fetchedAt };
    inFlight.whereToBuy = null;
    try {
      localStorage.setItem(LS_WTB_KEY, JSON.stringify({ data, fetchedAt }));
    } catch { /* ignore */ }
    notify();
    return data;
  }).catch(() => {
    inFlight.whereToBuy = null;
    return cache.whereToBuy?.data ?? [];
  });

  return inFlight.whereToBuy;
}

export function invalidateWhereToBuyCache() {
  cache.whereToBuy = null;
  inFlight.whereToBuy = null;
  try { localStorage.removeItem(LS_WTB_KEY); } catch { /* ignore */ }
}

export function invalidateWhereToBuyStorageCache() {
  try { localStorage.removeItem(LS_WTB_KEY); } catch { /* ignore */ }
}

// ─── Initialization ──────────────────────────────────────────────────────────
// Synchronously hydrate the in-memory cache from localStorage at module-load
// time. This is the difference between "blank cards for 300-1500ms while the
// DB fetch resolves" vs. "cards (and their WebP images) appear on first paint
// with the last-known data, then silently refresh in the background if DB
// returns newer data".

(function preloadFromStorage() {
  if (typeof localStorage === 'undefined') return;

  const products = safeReadLS<Product[]>(LS_PRODUCTS_KEY);
  if (products) cache.products = products;

  const portfolio = safeReadLS<PortfolioProject[]>(LS_PORTFOLIO_KEY);
  if (portfolio) cache.portfolio = portfolio;

  const news = safeReadLS<PublicNewsItem[]>(LS_NEWS_KEY);
  if (news) cache.news = news;

  const wtb = safeReadLS<PublicWTBPoint[]>(LS_WTB_KEY);
  if (wtb) cache.whereToBuy = wtb;
})();
